// Plain-string content helpers for booking emails: the store address block, its Google Maps
// deep-link, and the human-readable cancellation / no-show policy sentence. Pure TS (no React)
// so both the email send utils and `buildBookingCalendar` can share the address format.

type StoreAddressParts = {
    streetAddress: string;
    suburb: string;
    state: string;
    postcode: string;
};

/** Single-line Australian-style address, e.g. "12 Example St, Richmond VIC 3121". */
export const formatStoreAddress = ({
    streetAddress,
    suburb,
    state,
    postcode,
}: StoreAddressParts): string => `${streetAddress}, ${suburb} ${state} ${postcode}`;

/** "Open in Google Maps" link for a formatted address string. */
export const getMapsUrl = (address: string): string =>
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

type NoShowPolicyParts = {
    noShowThresholdMinutes: number;
    freeCancellationUntilHours: number;
};

/** One-sentence plain-English policy built from the store's booking settings. */
export const buildNoShowPolicyText = ({
    noShowThresholdMinutes,
    freeCancellationUntilHours,
}: NoShowPolicyParts): string => {
    const minutesClause =
        noShowThresholdMinutes === 0
            ? "If you're late without letting us know, your table may be released and the booking treated as a no-show."
            : `If you're more than ${noShowThresholdMinutes} minute${noShowThresholdMinutes === 1 ? "" : "s"} late ` +
              `without letting us know, your table may be released and the booking treated as a no-show.`;

    const cancellationClause =
        freeCancellationUntilHours === 0
            ? "To change or cancel, please do so before your reservation."
            : `To change or cancel, please do so at least ${freeCancellationUntilHours} hour${freeCancellationUntilHours === 1 ? "" : "s"} before your reservation.`;

    return `Please arrive on time. ${minutesClause} ${cancellationClause}`;
};
